"use client";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { businessItemsAtom } from "@/store";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { BusinessCard } from "@/app/(public)/applicators/_components/business-card";
import { SpecialtyCombobox } from "./specialty-combobox";
import { BusinessMapQuickNav } from "./business-map-quick-nav";
import { useAnalytics } from "@/components/providers/analytics-provider";
import { ChevronDown } from "lucide-react";

interface ApiBusinessItem {
  id: string;
  name: string;
  slug: string;
  city: string;
  state: string;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  rating?: number | null;
  review_count: number;
  specialties: string[];
  last_synced_at?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  google_place_id?: string | null;
  data_source?: string | null;
  plaster_services?: boolean;
  microcement_services?: boolean;
  venetian_plaster_services?: boolean;
  limewash_services?: boolean;
  paint_services?: boolean;
}

interface ApiResponse {
  items: ApiBusinessItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const SORT_OPTIONS = [
  { value: "rating_desc", label: "Rating High→Low" },
  { value: "rating_asc", label: "Rating Low→High" },
  { value: "reviews_desc", label: "Most Reviews" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
];

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

const SERVICE_OPTIONS = [
  { value: "plaster", label: "Plaster" },
  { value: "microcement", label: "Microcement" },
  { value: "venetian_plaster", label: "Venetian Plaster" },
  { value: "limewash", label: "Limewash" },
  { value: "paint", label: "Paint" },
];

export function BusinessFilters({
  slug,
  initialTotal,
}: {
  slug: string;
  initialTotal: number;
}) {
  const [search, setSearch] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [sort, setSort] = useState("rating_desc");
  const [page, setPage] = useState(1);
  const pageSize = 30;
  const debouncedSearch = useDebounce(search, 300);
  const setBusinessItems = useSetAtom(businessItemsAtom);

  const queryKey = useMemo(() => {
    const combinedRaw = [debouncedSearch, specialty]
      .filter(Boolean)
      .join(" ")
      .trim();
    const effectiveSearch = combinedRaw || ""; // default fallback
    return [
      "city-businesses",
      {
        slug,
        search: effectiveSearch,
        services: services.join(","),
        sort,
        page,
        pageSize,
      },
    ];
  }, [slug, debouncedSearch, specialty, services, sort, page, pageSize]);

  const { data, isLoading, isError } = useQuery<ApiResponse, Error>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        sort,
        page: String(page),
        pageSize: String(pageSize),
      });
      // Combine specialty + general search; fallback to '' when empty
      const combinedRaw = [debouncedSearch, specialty]
        .filter(Boolean)
        .join(" ")
        .trim();
      const effectiveSearch = combinedRaw || "";
      params.set("search", effectiveSearch);
      if (services.length > 0) {
        params.set("services", services.join(","));
      }
      const res = await fetch(
        `/api/applicators/city/${slug}/businesses?${params}`
      );
      if (!res.ok) throw new Error("Failed to load businesses");
      return (await res.json()) as ApiResponse;
    },
  });

  // Featured businesses for this city
  const {
    data: featuredData,
    isLoading: isLoadingFeatured,
    isError: isErrorFeatured,
  } = useQuery<
    { ok: boolean; items: ApiBusinessItem[]; fallback?: boolean },
    Error
  >({
    queryKey: ["city-featured", { slug }],
    queryFn: async () => {
      const res = await fetch(`/api/applicators/city/${slug}/featured`);
      if (!res.ok) throw new Error("Failed to load featured");
      return (await res.json()) as {
        ok: boolean;
        items: ApiBusinessItem[];
        fallback?: boolean;
      };
    },
  });

  const { trackTyped } = useAnalytics();
  useEffect(() => {
    if (featuredData?.fallback) {
      trackTyped("applicators_featured_fallback", { city_slug: slug });
    }
    // trackTyped is stable from context; lint suppression is safe here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredData?.fallback, slug]);

  // Memoize items array to keep stable reference for downstream useMemo dependencies
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? initialTotal;
  const totalPages = data?.totalPages ?? 1;
  const currentPage = data?.page ?? page;

  function reset() {
    setSearch("");
    setSpecialty("");
    setServices([]);
    setSort("rating_desc");
    setPage(1);
  }

  function toggleService(service: string) {
    setServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
    setPage(1);
  }

  // Prepare items for map/quick nav for current page only
  const navItems = items.map((b) => ({
    id: b.id,
    name: b.name,
    latitude: b.latitude,
    longitude: b.longitude,
    rating: b.rating,
    review_count: b.review_count,
    specialties: b.specialties || [],
  }));

  // Update the business items atom with indices (only for items with valid coordinates)
  useEffect(() => {
    const itemsWithIndices = items
      .filter((b) => b.latitude != null && b.longitude != null)
      .map((b, index) => ({
        id: b.id,
        index: index + 1, // 1-based index for display
      }));
    setBusinessItems(itemsWithIndices);
  }, [items, setBusinessItems]);

  function trackFiltersChange() {
    trackTyped("applicators_filter_change", {
      services: services.join(","),
      specialty: specialty || "",
      sort,
      city_slug: slug,
    });
  }

  // Track on significant filter change events
  useEffect(() => {
    if (!isLoading) trackFiltersChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.join(","), specialty, sort]);

  return (
    <div className="space-y-12">
      <div className="grid gap-6 md:grid-cols-6">
        <div className="space-y-2 md:col-span-2">
          <label className="text-sm font-medium">Search Businesses</label>
          <Input
            placeholder="e.g. Studio, Microcement, Venetian"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <SpecialtyCombobox
          value={specialty}
          onChange={(val) => {
            setSpecialty(val);
            setPage(1);
          }}
        />
        <div className="space-y-2">
          <label className="text-sm font-medium">Services</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between"
                aria-expanded={false}
              >
                <span className="truncate">
                  {services.length > 0
                    ? `${services.length} selected`
                    : "All Services"}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-2">
                {SERVICE_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={option.value}
                      checked={services.includes(option.value)}
                      onCheckedChange={() => toggleService(option.value)}
                    />
                    <label
                      htmlFor={option.value}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="business-sort">
            Sort
          </label>
          <select
            id="business-sort"
            className="w-full h-10 rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setPage(1);
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button variant="outline" className="w-full" onClick={reset}>
            Reset
          </Button>
        </div>
      </div>
      <div>
        {isLoading && (
          <div
            className="grid grid-cols-1 gap-10"
            aria-label="Loading businesses"
            aria-busy="true"
            role="status"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="border border-border rounded-lg bg-card overflow-hidden"
              >
                <div className="p-8 space-y-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 w-full max-w-[65%]">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                    <div className="space-y-2 text-right">
                      <Skeleton className="h-5 w-12" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-8">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <div className="space-y-1">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Skeleton key={j} className="h-3 w-full" />
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <div className="space-y-2">
                        {Array.from({ length: 4 }).map((_, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <Skeleton className="h-5 w-5 rounded-full" />
                            <Skeleton className="h-3 w-32" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2 md:items-end md:text-right">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <Skeleton key={j} className="h-3 w-40" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {isError && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="rounded-full bg-destructive/10 p-4 mb-4">
              <svg
                className="h-8 w-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">
              Unable to Load Businesses
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              We encountered an error while loading the businesses. Please try
              again or contact support if the problem persists.
            </p>
          </div>
        )}
      </div>
      {/* Map + Quick Navigation reflect current page & filters; hide while loading or no results */}
      {!isLoading && items.length > 0 && (
        <BusinessMapQuickNav items={navItems} slug={slug} />
      )}

      {/* Featured section under the map, styled like normal listings with green border/tag */}
      {!isLoadingFeatured &&
        !isErrorFeatured &&
        featuredData?.items &&
        featuredData.items.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-semibold mb-4">
              Featured Applicators
            </h2>
            <div className="grid grid-cols-1 gap-10">
              {featuredData.items.map((b) => (
                <div
                  key={b.id}
                  id={`featured-biz-${b.id}`}
                  data-featured
                  onClick={() =>
                    trackTyped("applicators_business_card_click", {
                      business_id: b.id,
                      source: "featured",
                      city_slug: slug,
                    })
                  }
                >
                  <BusinessCard
                    business={{
                      ...b,
                      specialties: b.specialties || [],
                      last_synced_at: b.last_synced_at
                        ? new Date(b.last_synced_at)
                        : null,
                    }}
                    isFeatured
                    citySlug={slug}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

      {!isLoading && !isError && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center border rounded-lg bg-muted/30">
          <div className="rounded-full bg-muted p-6 mb-6">
            <svg
              className="h-12 w-12 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2">No Businesses Found</h3>
          <p className="text-sm text-muted-foreground max-w-md mb-6">
            {search || specialty || services.length > 0
              ? "No businesses match your current filters. Try adjusting your search criteria or removing some filters."
              : "There are currently no businesses listed in this area."}
          </p>
          {(search || specialty || services.length > 0) && (
            <Button variant="outline" onClick={reset}>
              Clear All Filters
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-10">
        {!isLoading &&
          items.length > 0 &&
          items.map((b: ApiBusinessItem) => (
            <div
              key={b.id}
              id={`biz-${b.id}`}
              data-business-id={b.id}
              onClick={() =>
                trackTyped("applicators_business_card_click", {
                  business_id: b.id,
                  source: "list",
                  city_slug: slug,
                })
              }
            >
              <BusinessCard
                business={{
                  ...b,
                  last_synced_at: b.last_synced_at
                    ? new Date(b.last_synced_at)
                    : null,
                }}
                citySlug={slug}
              />
            </div>
          ))}
      </div>
      {!isLoading && items.length > 0 && (
        <div className="flex flex-col items-center justify-center mt-8 gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Badge variant="secondary" className="px-4 py-1">
              Page {currentPage} / {totalPages}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
          <Badge variant="secondary" className="px-4 py-1">
            Showing {items.length} of {total}
          </Badge>
        </div>
      )}
    </div>
  );
}
